import { Authenticated, Refine } from "@refinedev/core";
import routerProvider, { DocumentTitleHandler } from "@refinedev/react-router";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router";

import { Layout } from "@/components/Layout";
import { AuditPage } from "@/pages/audit";
import { DashboardPage } from "@/pages/dashboard";
import { InboxListPage } from "@/pages/inbox/list";
import { InboxShowPage } from "@/pages/inbox/show";
import { InterviewCallsPage } from "@/pages/interviews/list";
import { InterviewCallShowPage } from "@/pages/interviews/show";
import { LoginPage } from "@/pages/login";
import { MemoirsListPage } from "@/pages/memoirs/list";
import { MemoirShowPage } from "@/pages/memoirs/show";
import { OrdersListPage } from "@/pages/orders/list";
import { OrderShowPage } from "@/pages/orders/show";
import { PromptDeliveriesPage } from "@/pages/prompts/deliveries";
import { DeliveryShowPage } from "@/pages/prompts/deliveryShow";
import { SendPromptPage } from "@/pages/prompts/sendNow";
import { PromptTemplatesPage } from "@/pages/prompts/templates";
import { ShipmentsListPage } from "@/pages/shipments/list";
import { ShipmentShowPage } from "@/pages/shipments/show";
import { StoriesListPage } from "@/pages/stories/list";
import { StoryShowPage } from "@/pages/stories/show";
import { TranscriptionsPage } from "@/pages/stories/transcriptions";
import { SystemPage } from "@/pages/system";
import { FamiliesListPage } from "@/pages/users/families";
import { UsersListPage } from "@/pages/users/list";
import { UserShowPage } from "@/pages/users/show";
import { LOGIN_PATH, authProvider } from "@/providers/authProvider";
import { dataProvider } from "@/providers/dataProvider";

/**
 * Nazwy zasobów są angielskie, bo to ścieżki API; adresy w przeglądarce
 * i etykiety - polskie. Widok Druk (print_orders) powstaje osobno i nie ma
 * go tu celowo.
 */
const RESOURCES = [
  { name: "orders", list: "/zamowienia", show: "/zamowienia/:id", meta: { label: "Zamówienia" } },
  { name: "memoirs", list: "/opowiesci", show: "/opowiesci/:id", meta: { label: "Opowieści" } },
  { name: "prompt-templates", list: "/pytania", meta: { label: "Baza pytań" } },
  {
    name: "prompt-deliveries",
    list: "/wysylki-pytan",
    show: "/wysylki-pytan/:id",
    meta: { label: "Wysyłki pytań" },
  },
  {
    name: "interview-calls",
    list: "/rozmowy",
    show: "/rozmowy/:id",
    meta: { label: "Rozmowy telefoniczne" },
  },
  {
    name: "inbound-messages",
    list: "/skrzynka",
    show: "/skrzynka/:id",
    meta: { label: "Skrzynka do przypisania" },
  },
  { name: "stories", list: "/historie", show: "/historie/:id", meta: { label: "Historie" } },
  { name: "recordings", list: "/transkrypcje", meta: { label: "Kolejka transkrypcji" } },
  {
    name: "shipments",
    list: "/przesylki",
    show: "/przesylki/:id",
    meta: { label: "Przesyłki" },
  },
  { name: "users", list: "/uzytkownicy", show: "/uzytkownicy/:id", meta: { label: "Konta rodzin" } },
  { name: "families", list: "/rodziny", meta: { label: "Rodziny" } },
  { name: "audit-log", list: "/audyt", meta: { label: "Audyt" } },
];

export function App() {
  return (
    <BrowserRouter>
      <Refine
        dataProvider={dataProvider}
        authProvider={authProvider}
        routerProvider={routerProvider}
        resources={RESOURCES}
        options={{
          disableTelemetry: true,
          warnWhenUnsavedChanges: true,
          title: { text: "Opowiedzo - panel" },
        }}
      >
        <Routes>
          <Route
            element={
              <Authenticated key="panel" redirectOnFail={LOGIN_PATH}>
                <Layout />
              </Authenticated>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="/zamowienia" element={<OrdersListPage />} />
            <Route path="/zamowienia/:id" element={<OrderShowPage />} />
            <Route path="/opowiesci" element={<MemoirsListPage />} />
            <Route path="/opowiesci/:id" element={<MemoirShowPage />} />
            <Route path="/pytania" element={<PromptTemplatesPage />} />
            <Route path="/wysylki-pytan" element={<PromptDeliveriesPage />} />
            {/* Wysyłka na żądanie jest akcją, nie zasobem - stąd własny adres
                obok logu wysyłek, a nie pozycja w bocznym pasku. */}
            <Route path="/wyslij-pytanie" element={<SendPromptPage />} />
            <Route path="/wysylki-pytan/:id" element={<DeliveryShowPage />} />
            <Route path="/rozmowy" element={<InterviewCallsPage />} />
            <Route path="/rozmowy/:id" element={<InterviewCallShowPage />} />
            <Route path="/skrzynka" element={<InboxListPage />} />
            <Route path="/skrzynka/:id" element={<InboxShowPage />} />
            <Route path="/historie" element={<StoriesListPage />} />
            <Route path="/historie/:id" element={<StoryShowPage />} />
            <Route path="/transkrypcje" element={<TranscriptionsPage />} />
            <Route path="/przesylki" element={<ShipmentsListPage />} />
            <Route path="/przesylki/:id" element={<ShipmentShowPage />} />
            <Route path="/uzytkownicy" element={<UsersListPage />} />
            <Route path="/uzytkownicy/:id" element={<UserShowPage />} />
            <Route path="/rodziny" element={<FamiliesListPage />} />
            <Route path="/system" element={<SystemPage />} />
            <Route path="/audyt" element={<AuditPage />} />
          </Route>

          <Route
            element={
              <Authenticated key="logowanie" fallback={<Outlet />}>
                <Navigate to="/" replace />
              </Authenticated>
            }
          >
            <Route path={LOGIN_PATH} element={<LoginPage />} />
          </Route>

          {/* Nieznany adres w panelu wewnętrznym to zwykle stary bookmark. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <DocumentTitleHandler />
      </Refine>
    </BrowserRouter>
  );
}
